import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { addGsa, editGsa, uploadGsaLogo } from '../../../api/endpoints/system';
import type { Gsa } from '../../../api/types/system';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { PhoneField } from '../../../components/PhoneField';

const schema = z.object({
  name: z.string().min(1, 'Firma adı gerekli'),
  city: z.string().optional(),
  district: z.string().optional(),
  countryCode: z.string().optional(),
  address: z.string().optional(),
  gsm: z.string().optional(),
  email: z.string().optional(),
  taxOffice: z.string().optional(),
  taxNo: z.string().optional(),
  currentCode: z.string().optional(),
  gsaType: z.coerce.number(),
  masterGsaId: z.coerce.number(),
  defaultBank: z.string().optional(),
  /** Comma-separated in the UI; split to `string[]` on submit. */
  mappedBanks: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

/** `Gsa` list rows carry `gsaType` as the string enum; the Add/Edit request wants the int. */
const GSA_TYPE_TO_CODE: Record<string, number> = { AGENCY: 0, CORPORATE: 1, MASTER: 2 };

const emptyValues: FormValues = {
  name: '',
  city: '',
  district: '',
  countryCode: '',
  address: '',
  gsm: '',
  email: '',
  taxOffice: '',
  taxNo: '',
  currentCode: '',
  gsaType: 0,
  masterGsaId: 0,
  defaultBank: '',
  mappedBanks: '',
};

function toFormValues(gsa: Gsa | null | undefined): FormValues {
  if (!gsa) return emptyValues;
  return {
    name: gsa.name ?? '',
    city: gsa.city ?? '',
    district: gsa.district ?? '',
    countryCode: gsa.countryCode ?? '',
    address: gsa.address ?? '',
    gsm: gsa.gsm ?? '',
    email: gsa.email ?? '',
    taxOffice: gsa.taxOffice ?? '',
    taxNo: gsa.taxNo ?? '',
    currentCode: gsa.currentCode ?? '',
    gsaType: gsa.gsaType ? GSA_TYPE_TO_CODE[gsa.gsaType] ?? 0 : 0,
    masterGsaId: gsa.masterGsaId ?? 0,
    defaultBank: '',
    mappedBanks: '',
  };
}

/** Create + edit dialog for Firma (Acente/Gsa). `Gsa/EditGsa` / `Gsa/DeleteGsa` /
 *  `Gsa/UploadLogo` are wired best-effort (see api/endpoints/system.ts). Pass `gsa` to
 *  edit an existing row; omit it to create. */
export function GsaFormDialog({
  open,
  gsa,
  onClose,
}: {
  open: boolean;
  gsa?: Gsa | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!gsa?.id;
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const {
    control,
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: emptyValues });

  const buildPayload = (values: FormValues) => {
    const { mappedBanks, masterGsaId, ...rest } = values;
    return {
      ...rest,
      masterGsaId: masterGsaId || undefined,
      mappedBanks: mappedBanks
        ? mappedBanks.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
    };
  };

  useEffect(() => {
    if (open) {
      reset(toFormValues(gsa));
      setLogoFile(null);
    }
  }, [open, gsa, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isEdit && gsa?.id) {
        if (logoFile) await uploadGsaLogo(gsa.id, logoFile);
        return editGsa({ id: gsa.id, ...buildPayload(values) });
      }
      return addGsa(buildPayload(values));
    },
    onSuccess: () => {
      enqueueSnackbar(isEdit ? 'Firma güncellendi.' : 'Firma oluşturuldu.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['system', 'gsaList'] });
      onClose();
    },
  });

  const submit = handleSubmit((values) => mutation.mutate(values));

  return (
    <Dialog open={open} onClose={() => !mutation.isPending && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Firma Düzenle' : 'Yeni Firma'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Firma Adı"
                error={!!errors.name}
                helperText={errors.name?.message}
                {...register('name')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Şehir" {...register('city')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="İlçe" {...register('district')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Ülke Kodu" {...register('countryCode')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="gsm"
                render={({ field }) => (
                  <PhoneField
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    label="Cep Telefonu"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth size="small" label="Adres" {...register('address')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="E-posta" {...register('email')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Vergi Dairesi" {...register('taxOffice')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Vergi No" {...register('taxNo')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Cari Kod" {...register('currentCode')} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                control={control}
                name="gsaType"
                render={({ field }) => (
                  <TextField {...field} select fullWidth size="small" label="Firma Tipi">
                    <MenuItem value={0}>Acente</MenuItem>
                    <MenuItem value={1}>Kurumsal</MenuItem>
                    <MenuItem value={2}>Master</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Ana Firma No"
                helperText="0 = yok"
                {...register('masterGsaId')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField fullWidth size="small" label="Varsayılan Banka" {...register('defaultBank')} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                label="Eşlenen Bankalar"
                helperText="Virgülle ayırın"
                {...register('mappedBanks')}
              />
            </Grid>
            {isEdit && (
              <Grid size={{ xs: 12 }}>
                <Button variant="outlined" component="label" size="small">
                  {logoFile ? logoFile.name : 'Logo Yükle'}
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
                  />
                </Button>
                {gsa?.logo && !logoFile && (
                  <Typography variant="caption" sx={{ ml: 1 }} color="text.secondary">
                    Mevcut: {gsa.logo}
                  </Typography>
                )}
              </Grid>
            )}
          </Grid>
          <ApiErrorAlert error={mutation.error} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Vazgeç
        </Button>
        <Button onClick={submit} variant="contained" disabled={mutation.isPending}>
          {mutation.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
